import React, { useEffect, useRef, useState } from "react";

import {
  isSheetsCallbackStatus,
  sheetsStatusMessage,
} from "../lib/sheets-oauth";

function hashPath(): string {
  const hash = window.location.hash.replace(/^#/, "");
  const queryIndex = hash.indexOf("?");
  return queryIndex === -1 ? hash : hash.slice(0, queryIndex);
}

/**
 * MOSA-146. The API redirects back with `?google_sheets=<status>`. Show the
 * result once, then strip the query so a reload does not replay it.
 *
 * On `/connect/sheets` the wizard's "Connected as" row already covers a
 * successful connect — do not stack a second banner there.
 */
const SheetsCallbackBanner: React.FC = () => {
  const [status, setStatus] = useState<string | null>(null);
  const shownPathRef = useRef<string | null>(null);

  useEffect(() => {
    const read = () => {
      const path = hashPath();
      // Clear after navigation away from the page that showed the banner.
      if (shownPathRef.current && shownPathRef.current !== path) {
        setStatus(null);
        shownPathRef.current = null;
      }

      const hash = window.location.hash.replace(/^#/, "");
      const queryIndex = hash.indexOf("?");
      if (queryIndex === -1) {
        return;
      }
      const params = new URLSearchParams(hash.slice(queryIndex + 1));
      const value = params.get("google_sheets");
      if (!isSheetsCallbackStatus(value)) {
        return;
      }
      params.delete("google_sheets");
      const rest = params.toString();
      window.history.replaceState(
        null,
        "",
        `#${path}${rest ? `?${rest}` : ""}`,
      );
      // Wizard has its own Connected-as row; skip the global banner there.
      if (path === "/connect/sheets") {
        return;
      }
      setStatus(value);
      shownPathRef.current = path;
    };
    read();
    window.addEventListener("hashchange", read);
    return () => window.removeEventListener("hashchange", read);
  }, []);

  if (!status || !isSheetsCallbackStatus(status)) {
    return null;
  }

  const tone =
    status === "connected"
      ? "border-od-success bg-od-success/10"
      : status === "cancelled"
        ? "border-od-border bg-od-surface-2"
        : "border-od-danger bg-od-danger/10";

  return (
    <div
      role="status"
      data-testid="sheets-oauth-status"
      className={`mb-3 rounded-md border px-3 py-2 text-sm text-od-text ${tone}`}
    >
      {sheetsStatusMessage(status)}
    </div>
  );
};

export default SheetsCallbackBanner;
